import * as angular from 'angular';

import { Group, TagDateValueWithTransaction, ResultsStatus, ActualDataType, ValidationScope, ReprocessingTaskOptionsOverride, InputValuesDTO } from '../../models';
import { IDateRangeService, ITranslatedLoggerService } from '../general';
import { TagManualInputValues } from '../../models/tagManualInputValues';
import { guidEmpty } from '../../common';
import { IRequestService } from '../../services';

export interface IManualInputsService {
    save(values: TagManualInputValues[], launchTask?: boolean, taskOptionsOverride?: ReprocessingTaskOptionsOverride): angular.IPromise<number>;
    saveRange: (values: IBulkManualInputValues) => angular.IPromise<number>;
    getManualInputs(series: string[], beginDate: string, endDate: string, force: boolean): angular.IPromise<TagDateValueWithTransaction[]>;
    getLastManualInputsStatuses(): angular.IPromise<ManualInputStatus>;
    isApprovalAvailable(hasApprobation: boolean, selectedGroup: Group, group: Group): boolean;
}

export interface ManualInputStatus {
    numberOfMissingData: number;
    status: ResultsStatus;
}

export interface IBulkManualInputValues {
    beginDate: string;
    endDate: string;
    values: { guid: string; value: number }[];
    includeEndDate: boolean;
}

export class ManualInputsService implements IManualInputsService {
    static serviceId = 'manualInputsService';

    static $inject = [
        '$q',
        'dateRangeService',
        'requestService',
        'translatedLoggerService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _dateRangeService: IDateRangeService,
        private _requestService: IRequestService,
        private _logger: ITranslatedLoggerService
    ) {
    }

    save(inputList: TagManualInputValues[], launchTask: boolean = true, taskOptionsOverride: ReprocessingTaskOptionsOverride = null): angular.IPromise<number> {
        const dtoList: InputValuesDTO[] = [];
        this._requestService.cleanAllCache();
        inputList.forEach(input => {
            input.values.forEach(va => {
                if ((va.newValue !== null || (va.value || va.value === 0)) && va.newValue !== undefined && va.newValue !== va.value) {
                    const dto: any = {
                        timeStamp: va.date.toISOString(),
                        tagDefinitionGuid: input.guid,
                        average: va.newValue
                    };
                    if (input.dataType === ActualDataType.Digital) {
                        dto.interpolated = va.newValue;
                    }
                    dtoList.push(dto);
                }
            });
        });

        return this.postSave(dtoList, launchTask, taskOptionsOverride);
    }

    saveRange(values: IBulkManualInputValues): angular.IPromise<number> {
        const url = this._requestService.getUrlV1() + 'manualInputs/postBulk/';
        return this._requestService.post(url, values, undefined, true);
    }

    getManualInputs(
        series: string[],
        beginDate: string,
        endDate: string,
        force: boolean
    ): angular.IPromise<TagDateValueWithTransaction[]> {
        if (this._dateRangeService.initialized()) {
            const url = 'manualInputs/getAllManualInputs/?beginDateTime=' + beginDate + '&endDateTime=' + endDate;
            if (force) {
                this._requestService.cleanCachedPostValue(url, series);
            }
            return this._requestService.cachedPostAtV1(url, series);
        } else {
            return this._$q.reject('Date range not initialized.');
        }
    }

    getLastManualInputsStatuses(): angular.IPromise<ManualInputStatus> {
        if (this._dateRangeService.initialized()) {
            const url = this._requestService.getUrlV1() + 'manualInputs/getLastManualInputsStatuses/?endDateTime=' + this._dateRangeService.endDate();
            return this._requestService.get(url);
        } else {
            return this._$q.reject('Date range not initialized.');
        }
    }

    isApprovalAvailable(enableApproval: boolean, selectedGroup: Group, group: Group): boolean {
        // Check is approval is enabled
        let isAvailable = enableApproval;

        // check approval strategy
        if (isAvailable) {
            if (!group || !group.approvalStrategyGuid || group.approvalStrategyGuid === guidEmpty) {
                isAvailable = false;
            }
        }

        // check if group has higher frequency then selected group
        if (isAvailable && group && selectedGroup) {
            if (selectedGroup.frequency.intervalTimeUnit < group.frequency.intervalTimeUnit
                || (selectedGroup.frequency.intervalTimeUnit === group.frequency.intervalTimeUnit && selectedGroup.frequency.interval <= group.frequency.interval)) {
                isAvailable = false;
            }
        } else {
            isAvailable = false;
        }

        // check if current group is Single period validation scope
        const isSinglePeriod = selectedGroup?.validationScope !== ValidationScope.MultiplePeriod;
        if (isAvailable && !isSinglePeriod) {
            isAvailable = false;
            this._logger.logError('Approval not compatible with multi-period validation page');
        }

        return isAvailable;
    }

    private postSave(dtoList: InputValuesDTO[], launchTask: boolean = true, taskOptionsOverride: ReprocessingTaskOptionsOverride = null): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'manualInputs/';
        const params = { launchTask: !!launchTask };
        return this._requestService.post(url, { values: dtoList, taskOptionsOverride: taskOptionsOverride }, params, true);
    }
}